import React from "react";
import type {
  MembershipPricingOption,
  Price,
  PricingOption
} from "@shared/graphql.types";

import { Accordion } from "packages/ui-shared/components/Accordion/Accordion";
import { DeprecatedUISharedButton } from "packages/ui-shared/components/Button/Button";

import {
  membershipContentContainer,
  membershipContentFooter,
  membershipContentHeader,
  priceExplainerStyles
} from "./Pricing.css";
import { PAGE_ELEMENT_CLICKED_EVENT_KEY } from "tracking/hooks/usePageElementEvents";
import { dispatch } from "@shared/utils.events";
import { markHydrationComplete } from "basket_and_checkout/shared/utils/markHydrationComplete.ref";
import { MarkdownRenderer } from "@shared/ui.primitives";
import { ChevronDownIcon, ChevronUpIcon } from "@shared/ui.icons";
import { colours } from "@shared/ui.design-system";

const PRICING_COMPONENT_RADIO_NAME = "pricing-toggle";

type AccordionWithSingleMembershipOptionProps = {
  selectedValue?: string;
  onAccordionChange: (value: string) => void;
  prices: Price[];
  setShowMembershipOptions: (sku: string | null) => void;
  defaultMembershipSku?: string | null;
  context?: string;
};

export const AccordionWithSingleMembershipOption: React.FC<
  AccordionWithSingleMembershipOptionProps
> = ({
  selectedValue,
  onAccordionChange,
  prices,
  setShowMembershipOptions,
  defaultMembershipSku,
  context
}) => {
  const radioName =
    PRICING_COMPONENT_RADIO_NAME + (context ? `-${context}` : "");

  return (
    <Accordion
      selectedValue={selectedValue}
      onChange={onAccordionChange}
      radioName={radioName}
    >
      {prices.map(
        ({
          price,
          id
        }: {
          price: MembershipPricingOption | PricingOption;
          id: string;
        }) => {
          const priceMembership =
            "membership" in price ? price.membership : null;

          return (
            <Accordion.Item value={id} key={id} radioName={radioName}>
              <Accordion.Item.Label
                value={id}
                header={price.title}
                description={price.subtitle ?? ""}
                rightContent={price.priceText}
                radioName={radioName}
                icon={
                  priceMembership &&
                  (selectedValue === id ? (
                    <ChevronUpIcon
                      height={20}
                      width={20}
                      fill={colours.primary.medium}
                    />
                  ) : (
                    <ChevronDownIcon
                      height={20}
                      width={20}
                      fill={colours.primary.medium}
                    />
                  ))
                }
              />
              {priceMembership && (
                <Accordion.Item.Content>
                  <div className={membershipContentContainer}>
                    <header className={membershipContentHeader}>
                      <b>{priceMembership.name}</b>
                    </header>
                    <div className={priceExplainerStyles}>
                      {priceMembership.priceExplainer}
                    </div>
                    <MarkdownRenderer content={priceMembership.perks} />

                    {priceMembership.cta && (
                      <DeprecatedUISharedButton
                        buttonRef={markHydrationComplete}
                        handleClick={() => {
                          dispatch(PAGE_ELEMENT_CLICKED_EVENT_KEY, {
                            target: "og_pdp_panel",
                            details: "view_link",
                            location: "og_pdp_panel",
                            locationDetails: defaultMembershipSku,
                            target2: defaultMembershipSku,
                            details2: priceMembership?.cta?.title ?? ""
                          });
                          setShowMembershipOptions(priceMembership.sku);
                        }}
                        variant="link"
                      >
                        {priceMembership.cta.title}
                      </DeprecatedUISharedButton>
                    )}
                  </div>
                  {priceMembership &&
                    selectedValue &&
                    priceMembership.terms?.termsContent && (
                      <MarkdownRenderer
                        className={membershipContentFooter}
                        as="footer"
                        content={priceMembership.terms.termsContent}
                      />
                    )}
                </Accordion.Item.Content>
              )}
            </Accordion.Item>
          );
        }
      )}
    </Accordion>
  );
};
